import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Calendar,
  Users,
  Trophy,
  Plus,
  Eye,
  Edit,
  Trash2,
  Sparkles,
  Swords,
  Gamepad2,
} from "lucide-react";
import { generateClient } from "aws-amplify/api";
import {
  listEvents,
  listLeaguePlayerStats,
  listLeaguePointAdjustments,
  listLeagues,
} from "../../graphql/queries";
import {
  deleteLeague,
  deleteLeaguePlayerStats,
  deleteLeaguePointAdjustment,
} from "../../graphql/mutations";
import { deleteEventCascade } from "../../lib/api/deleteEventCascade";
import { formatDate } from "../../utils/dateUtils";
import {
  LEAGUE_GAMES,
  LeagueGame,
  getLeagueGame,
  getLeagueGameLabel,
} from "../../lib/leagueGames";

const client = generateClient();

const LeagueManagement: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [gameFilter, setGameFilter] = useState<"ALL" | LeagueGame>("ALL");
  const [deletingLeagueId, setDeletingLeagueId] = useState<string | null>(null);

  const {
    data: leagues,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["leagues"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listLeagues,
        variables: {
          limit: 100,
        },
      });
      return result.data.listLeagues.items;
    },
  });

  const getStatusBadge = (isActive: boolean, endDate: string | null) => {
    if (!isActive) {
      return <Badge variant="secondary">Inactive</Badge>;
    }
    if (endDate && new Date(endDate) < new Date()) {
      return <Badge variant="destructive">Ended</Badge>;
    }
    return <Badge variant="default">Active</Badge>;
  };

  const filteredLeagues =
    leagues?.filter(
      (league: any) =>
        gameFilter === "ALL" || getLeagueGame(league.game) === gameFilter
    ) ?? [];

  const handleDeleteLeague = async (league: any) => {
    if (
      !window.confirm(
        `Delete "${league.name}"?\n\nThis permanently deletes the league, all of its events and results, saved standings, and point adjustments. This cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingLeagueId(league.id);
    try {
      const eventsResult = await client.graphql({
        query: listEvents,
        variables: {
          filter: { leagueId: { eq: league.id } },
          limit: 1000,
        },
      });
      for (const event of eventsResult.data.listEvents.items) {
        await deleteEventCascade(event.id);
      }

      const [statsResult, adjustmentsResult] = await Promise.all([
        client.graphql({
          query: listLeaguePlayerStats,
          variables: {
            filter: { leagueId: { eq: league.id } },
            limit: 1000,
          },
        }),
        client.graphql({
          query: listLeaguePointAdjustments,
          variables: {
            filter: { leagueId: { eq: league.id } },
            limit: 1000,
          },
        }),
      ]);

      for (const stat of statsResult.data.listLeaguePlayerStats.items) {
        await client.graphql({
          query: deleteLeaguePlayerStats,
          variables: { input: { id: stat.id } },
        });
      }
      for (const adjustment of adjustmentsResult.data.listLeaguePointAdjustments
        .items) {
        await client.graphql({
          query: deleteLeaguePointAdjustment,
          variables: { input: { id: adjustment.id } },
        });
      }
      await client.graphql({
        query: deleteLeague,
        variables: { input: { id: league.id } },
      });
      await queryClient.invalidateQueries();
    } catch (deleteError) {
      console.error("Failed to delete league:", deleteError);
      alert("The league could not be deleted. Please try again.");
    } finally {
      setDeletingLeagueId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">
          Error loading leagues: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-0 sm:p-2">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">
            Admin workspace
          </p>
          <h1 className="text-3xl font-bold tracking-tight">
            League Management
          </h1>
          <p className="text-muted-foreground mt-2">
            Create and manage leagues for every supported game
          </p>
        </div>
        <Button
          size="lg"
          className="shadow-lg shadow-primary/20"
          onClick={() => navigate("/admin/leagues/create")}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create League
        </Button>
      </div>

      {leagues && leagues.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2 rounded-2xl border border-white/10 bg-card/60 p-2 backdrop-blur-xl">
          {[
            { value: "ALL" as const, label: "All leagues", icon: Trophy },
            ...(Object.keys(LEAGUE_GAMES) as LeagueGame[]).map((game) => ({
              value: game,
              label: LEAGUE_GAMES[game].shortLabel,
              icon:
                game === "MAGIC_THE_GATHERING"
                  ? Sparkles
                  : game === "RIFTBOUND"
                  ? Swords
                  : Gamepad2,
            })),
          ].map((option) => {
            const Icon = option.icon;
            return (
              <Button
                key={option.value}
                size="sm"
                variant={gameFilter === option.value ? "default" : "ghost"}
                onClick={() => setGameFilter(option.value)}
              >
                <Icon className="mr-2 h-4 w-4" />
                {option.label}
              </Button>
            );
          })}
        </div>
      )}

      {filteredLeagues.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLeagues.map((league: any) => (
            <Card
              key={league.id}
              className="group border-white/10 bg-card/75 transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/10"
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{league.name}</CardTitle>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Badge variant="secondary">
                        {getLeagueGameLabel(league.game)}
                      </Badge>
                      <Trophy className="h-4 w-4 text-primary" />
                      <span className="text-sm text-muted-foreground">
                        {league.format}
                      </span>
                    </div>
                  </div>
                  {getStatusBadge(league.isActive, league.endDate)}
                </div>
              </CardHeader>
              <CardContent>
                {league.description && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {league.description}
                  </p>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {formatDate(league.startDate)}
                      {league.endDate && ` - ${formatDate(league.endDate)}`}
                    </span>
                  </div>

                  {league.maxParticipants && (
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-4 w-4" />
                      <span>Max: {league.maxParticipants} players</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/admin/leagues/${league.id}`)}
                    className="flex-1"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/admin/leagues/${league.id}/edit`)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={deletingLeagueId === league.id}
                    onClick={() => handleDeleteLeague(league)}
                    aria-label={`Delete ${league.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center py-12">
          <CardContent>
            <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {leagues?.length ? "No matching leagues" : "No Leagues Yet"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {leagues?.length
                ? "Choose another game filter to see your leagues"
                : "Create your first league to get started"}
            </p>
            {!leagues?.length && (
              <Button onClick={() => navigate("/admin/leagues/create")}>
                <Plus className="h-4 w-4 mr-2" />
                Create League
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LeagueManagement;
